// src/hooks/mediaInsights/useMediaInsights.ts

import { useQuery } from "@tanstack/react-query";
import {
  getAllChannelsQueryOptions,
} from "@/services/queries/mediaInsights/insights";
import type {
  GetAllChannelsResponse,
} from "@/services/queries/mediaInsights/insights";

import {
  allVideosQueryOptions,
  videoDetailQueryOptions,
  channelVideosQueryOptions,
} from "@/services/queries/mediaInsights/youtube";
import type {
  YouTubeVideoDetailResponse,
  YouTubeVideoListResponse,
} from "@/services/queries/mediaInsights/youtube";

// =============================================================================
// Channel Hooks
// =============================================================================

export interface UseGetAllChannelsResult {
  data: GetAllChannelsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useGetAllChannels(): UseGetAllChannelsResult {
  const query = useQuery({
    ...getAllChannelsQueryOptions(),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// =============================================================================
// YouTube Video Hooks
// =============================================================================

export interface UseAllVideosResult {
  data: YouTubeVideoListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface UseVideoDetailResult {
  data: YouTubeVideoDetailResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface UseChannelVideosResult {
  data: YouTubeVideoListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch all videos (for home page)
 */
export function useAllVideos(): UseAllVideosResult {
  const query = useQuery({
    ...allVideosQueryOptions(),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch full video details with sections and tickers
 */
export function useVideoDetail(
  videoId: string,
  includeSections = true,
  includeTickers = true
): UseVideoDetailResult {
  const query = useQuery({
    ...videoDetailQueryOptions(videoId, includeSections, includeTickers),
    enabled: !!videoId && videoId.trim().length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch all videos for a channel (for video page sidebar)
 */
export function useChannelVideos(
  channelId: string,
  limit = 50,
  offset = 0
): UseChannelVideosResult {
  const query = useQuery({
    ...channelVideosQueryOptions(channelId, limit, offset),
    enabled: !!channelId && channelId.trim().length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
